import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  romCode: '',
  displayName: '',
  unit: 'degrees',
  sideSpecific: false,
  required: true,
  maxDailyIncrease: 5,
  minimumValue: '',
  maximumValue: '',
  defaultMeasurementValue: '',
  unchangedNoteLimit: 2,
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    romCode: template?.romCode || '',
    displayName: template?.displayName || '',
    unit: template?.unit || '',
    sideSpecific: Boolean(template?.sideSpecific),
    required: Boolean(template?.required),
    maxDailyIncrease:
      template?.maxDailyIncrease === null ||
      template?.maxDailyIncrease === undefined
        ? 0
        : Number(template.maxDailyIncrease),
    minimumValue:
      template?.minimumValue === null || template?.minimumValue === undefined
        ? ''
        : String(template.minimumValue),
    maximumValue:
      template?.maximumValue === null || template?.maximumValue === undefined
        ? ''
        : String(template.maximumValue),
    defaultMeasurementValue:
      template?.defaultMeasurementValue === null ||
      template?.defaultMeasurementValue === undefined
        ? ''
        : String(template.defaultMeasurementValue),
    unchangedNoteLimit:
      template?.unchangedNoteLimit === null ||
      template?.unchangedNoteLimit === undefined
        ? 0
        : Number(template.unchangedNoteLimit),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function toOptionalInteger(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  return Number(value);
}

function validateForm(form, bodyPartMap) {
  const errors = {};
  const bodyPartTemplateId = Number(form.bodyPartTemplateId);
  const bodyPart = bodyPartMap.get(bodyPartTemplateId);
  const romCode = String(form.romCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const unit = String(form.unit || '').trim();
  const maxDailyIncrease = Number(form.maxDailyIncrease);
  const minimumValue = toOptionalInteger(form.minimumValue);
  const maximumValue = toOptionalInteger(form.maximumValue);
  const defaultMeasurementValue = toOptionalInteger(form.defaultMeasurementValue);
  const unchangedNoteLimit = Number(form.unchangedNoteLimit);
  const displayOrder = Number(form.displayOrder);
  const adminNotes = String(form.adminNotes || '').trim();

  if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
    errors.bodyPartTemplateId = 'Select a body part.';
  }

  if (!romCode) {
    errors.romCode = 'ROM code is required.';
  } else if (romCode.length > 100) {
    errors.romCode = 'ROM code cannot exceed 100 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(romCode)) {
    errors.romCode =
      'ROM code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'ROM display name is required.';
  } else if (displayName.length > 150) {
    errors.displayName = 'ROM display name cannot exceed 150 characters.';
  }

  if (!unit) {
    errors.unit = 'ROM unit is required.';
  } else if (unit.length > 30) {
    errors.unit = 'ROM unit cannot exceed 30 characters.';
  }

  if (form.sideSpecific && bodyPart && !bodyPart.supportsLaterality) {
    errors.sideSpecific =
      'This body part does not support laterality, so ROM cannot be side-specific.';
  }

  if (!Number.isInteger(maxDailyIncrease) || maxDailyIncrease < 0) {
    errors.maxDailyIncrease =
      'Maximum daily increase must be a whole number of 0 or greater.';
  }

  if (minimumValue !== null && !Number.isInteger(minimumValue)) {
    errors.minimumValue = 'Minimum value must be a whole number.';
  }

  if (maximumValue !== null && !Number.isInteger(maximumValue)) {
    errors.maximumValue = 'Maximum value must be a whole number.';
  }

  if (
    defaultMeasurementValue !== null &&
    !Number.isInteger(defaultMeasurementValue)
  ) {
    errors.defaultMeasurementValue = 'Default value must be a whole number.';
  }

  if (
    !errors.minimumValue &&
    !errors.maximumValue &&
    minimumValue !== null &&
    maximumValue !== null &&
    minimumValue > maximumValue
  ) {
    errors.maximumValue =
      'Maximum value must be greater than or equal to minimum value.';
  }

  if (
    !errors.defaultMeasurementValue &&
    !errors.minimumValue &&
    defaultMeasurementValue !== null &&
    minimumValue !== null &&
    defaultMeasurementValue < minimumValue
  ) {
    errors.defaultMeasurementValue =
      'Default value cannot be lower than the minimum value.';
  }

  if (
    !errors.defaultMeasurementValue &&
    !errors.maximumValue &&
    defaultMeasurementValue !== null &&
    maximumValue !== null &&
    defaultMeasurementValue > maximumValue
  ) {
    errors.defaultMeasurementValue =
      'Default value cannot be higher than the maximum value.';
  }

  if (!Number.isInteger(unchangedNoteLimit) || unchangedNoteLimit < 0) {
    errors.unchangedNoteLimit =
      'Unchanged-note limit must be a whole number of 0 or greater.';
  }

  if (!Number.isInteger(displayOrder) || displayOrder < 0) {
    errors.displayOrder =
      'Display order must be a whole number of 0 or greater.';
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  if (form.active && bodyPart && !bodyPart.active) {
    errors.active =
      'An active ROM template cannot belong to an inactive body part.';
  }

  return errors;
}

function buildRequest(form) {
  return {
    bodyPartTemplateId: Number(form.bodyPartTemplateId),
    romCode: String(form.romCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    unit: String(form.unit || '').trim(),
    sideSpecific: Boolean(form.sideSpecific),
    required: Boolean(form.required),
    maxDailyIncrease: Number(form.maxDailyIncrease),
    minimumValue: toOptionalInteger(form.minimumValue),
    maximumValue: toOptionalInteger(form.maximumValue),
    defaultMeasurementValue: toOptionalInteger(form.defaultMeasurementValue),
    unchangedNoteLimit: Number(form.unchangedNoteLimit),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: String(form.adminNotes || '').trim() || null,
  };
}

export default function RomTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [selectedBodyPartId, setSelectedBodyPartId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.romTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const selectedBodyPart = useMemo(
    () => bodyPartMap.get(Number(selectedBodyPartId)) || null,
    [bodyPartMap, selectedBodyPartId]
  );

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          template.romTemplateId,
          template.romCode,
          template.displayName,
          template.unit,
          template.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, searchTerm, statusFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  useEffect(() => {
    loadBodyParts();
  }, []);

  useEffect(() => {
    if (selectedBodyPartId) {
      loadTemplates(selectedBodyPartId);
    } else {
      setTemplates([]);
      setLoading(false);
    }
  }, [selectedBodyPartId]);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadBodyParts = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const bodyPartData =
        await clinicalTemplateAdminService.getAllBodyPartTemplates();
      const normalized = Array.isArray(bodyPartData) ? bodyPartData : [];

      setBodyParts(normalized);

      if (normalized.length > 0) {
        const firstActive = normalized.find((bodyPart) => bodyPart.active);
        const initial = firstActive || normalized[0];

        setSelectedBodyPartId((current) =>
          current || String(initial.bodyPartTemplateId)
        );

        setForm((current) => ({
          ...current,
          bodyPartTemplateId:
            current.bodyPartTemplateId || String(initial.bodyPartTemplateId),
        }));
      } else {
        setLoading(false);
      }
    } catch (error) {
      setBodyParts([]);
      setTemplates([]);
      setLoadError(error?.message || 'Body-part configuration could not be loaded.');
      setLoading(false);
    }
  };

  const loadTemplates = async (bodyPartTemplateId) => {
    try {
      setLoading(true);
      setLoadError('');

      const templateData =
        await clinicalTemplateAdminService.getAllRomTemplates(
          bodyPartTemplateId
        );

      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      setTemplates([]);
      setLoadError(
        error?.message || 'ROM-template configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({
      ...EMPTY_FORM,
      bodyPartTemplateId: selectedBodyPartId || '',
    });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setSelectedBodyPartId(String(template.bodyPartTemplateId));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleFormBodyPartChange = (value) => {
    const bodyPart = bodyPartMap.get(Number(value));

    setForm((current) => ({
      ...current,
      bodyPartTemplateId: value,
      sideSpecific:
        current.sideSpecific && bodyPart && !bodyPart.supportsLaterality
          ? false
          : current.sideSpecific,
      active: current.active && bodyPart && !bodyPart.active ? false : current.active,
    }));

    setSelectedBodyPartId(value);
    setFormErrors({});
    setActionError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form, bodyPartMap);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateRomTemplate(
          editingTemplate.romTemplateId,
          request
        );
        showSuccess('ROM template updated.');
      } else {
        await clinicalTemplateAdminService.createRomTemplate(request);
        showSuccess('ROM template created.');
      }

      const bodyPartId = String(request.bodyPartTemplateId);
      setSelectedBodyPartId(bodyPartId);
      setEditingTemplate(null);
      setForm({
        ...EMPTY_FORM,
        bodyPartTemplateId: bodyPartId,
      });
      setFormErrors({});
      setActionError('');
      await loadTemplates(bodyPartId);
    } catch (error) {
      setActionError(error?.message || 'ROM template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;
    const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

    if (nextActive && bodyPart && !bodyPart.active) {
      setActionError(
        `"${bodyPart.displayName}" is inactive. Activate the body part before activating this ROM template.`
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Historical clinical notes will keep their existing ROM template reference.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.romTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setRomTemplateActive(
        template.romTemplateId,
        nextActive
      );

      showSuccess(`ROM template ${nextActive ? 'activated' : 'deactivated'}.`);

      if (editingTemplate?.romTemplateId === template.romTemplateId) {
        setForm((current) => ({
          ...current,
          active: nextActive,
        }));
        setEditingTemplate((current) => ({
          ...current,
          active: nextActive,
        }));
      }

      await loadTemplates(template.bodyPartTemplateId);
    } catch (error) {
      setActionError(
        error?.message ||
          `ROM template could not be ${nextActive ? 'activated' : 'deactivated'}.`
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="rom-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">ROM CONFIGURATION</span>
            <h2>{isEditing ? 'Edit ROM Template' : 'Create ROM Template'}</h2>
            <p>
              Configure reusable range-of-motion measurements for one body part.
              Side-specific measurements are permitted only when the body-part
              template supports laterality.
            </p>
          </div>

          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {actionError && (
          <div className="clinical-template-message error" role="alert">
            {actionError}
          </div>
        )}

        {success && (
          <div className="clinical-template-message success" role="status">
            {success}
          </div>
        )}

        <form className="rom-template-form" onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body Part
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  handleFormBodyPartChange(event.target.value)
                }
                disabled={isEditing || saving}
              >
                <option value="">Select body part</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
              {isEditing && (
                <small>Body-part assignment cannot be changed after creation.</small>
              )}
            </label>

            <label>
              ROM Code
              <input
                type="text"
                value={form.romCode}
                onChange={(event) =>
                  updateFormField('romCode', event.target.value.toUpperCase())
                }
                maxLength={100}
                disabled={isEditing || saving}
                placeholder="DORSIFLEXION"
              />
              {formErrors.romCode && (
                <span className="clinical-template-field-error">
                  {formErrors.romCode}
                </span>
              )}
              {isEditing && <small>ROM code cannot be changed after creation.</small>}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={150}
                disabled={saving}
                placeholder="Dorsiflexion"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Unit
              <input
                type="text"
                value={form.unit}
                onChange={(event) => updateFormField('unit', event.target.value)}
                maxLength={30}
                disabled={saving}
                placeholder="degrees"
              />
              {formErrors.unit && (
                <span className="clinical-template-field-error">
                  {formErrors.unit}
                </span>
              )}
            </label>

            <label>
              Maximum Daily Increase
              <input
                type="number"
                min="0"
                step="1"
                value={form.maxDailyIncrease}
                onChange={(event) =>
                  updateFormField('maxDailyIncrease', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.maxDailyIncrease && (
                <span className="clinical-template-field-error">
                  {formErrors.maxDailyIncrease}
                </span>
              )}
              <small>Current normal ROM configuration is 5 degrees.</small>
            </label>

            <label>
              Unchanged-Note Warning Limit
              <input
                type="number"
                min="0"
                step="1"
                value={form.unchangedNoteLimit}
                onChange={(event) =>
                  updateFormField('unchangedNoteLimit', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.unchangedNoteLimit && (
                <span className="clinical-template-field-error">
                  {formErrors.unchangedNoteLimit}
                </span>
              )}
              <small>0 disables repeated-value warnings.</small>
            </label>

            <label>
              Minimum Value
              <input
                type="number"
                step="1"
                value={form.minimumValue}
                onChange={(event) =>
                  updateFormField('minimumValue', event.target.value)
                }
                disabled={saving}
                placeholder="Optional"
              />
              {formErrors.minimumValue && (
                <span className="clinical-template-field-error">
                  {formErrors.minimumValue}
                </span>
              )}
            </label>

            <label>
              Maximum Value
              <input
                type="number"
                step="1"
                value={form.maximumValue}
                onChange={(event) =>
                  updateFormField('maximumValue', event.target.value)
                }
                disabled={saving}
                placeholder="Optional"
              />
              {formErrors.maximumValue && (
                <span className="clinical-template-field-error">
                  {formErrors.maximumValue}
                </span>
              )}
            </label>


            <label>
              Default Draft Value
              <input
                type="number"
                step="1"
                value={form.defaultMeasurementValue}
                onChange={(event) =>
                  updateFormField('defaultMeasurementValue', event.target.value)
                }
                disabled={saving}
                placeholder="Optional"
              />
              {formErrors.defaultMeasurementValue && (
                <span className="clinical-template-field-error">
                  {formErrors.defaultMeasurementValue}
                </span>
              )}
              <small>
                Optional starting value shown on a brand-new Draft. It is not
                patient documentation until the clinician saves the ROM row.
              </small>
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.sideSpecific}
                onChange={(event) =>
                  updateFormField('sideSpecific', event.target.checked)
                }
                disabled={
                  saving ||
                  (Boolean(form.bodyPartTemplateId) &&
                    selectedBodyPart &&
                    !selectedBodyPart.supportsLaterality)
                }
              />
              <span>
                <strong>Side Specific</strong>
                <small>Record separate LEFT / RIGHT measurements.</small>
                {formErrors.sideSpecific && (
                  <span className="clinical-template-field-error">
                    {formErrors.sideSpecific}
                  </span>
                )}
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(event) =>
                  updateFormField('required', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Required</strong>
                <small>
                  This measurement must be completed before final validation.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>Available for newly created clinical notes.</small>
                {formErrors.active && (
                  <span className="clinical-template-field-error">
                    {formErrors.active}
                  </span>
                )}
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save ROM Template'
                  : 'Create ROM Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">CONFIGURED ROM TEMPLATES</span>
            <h2>ROM Templates</h2>
            <p>
              {selectedBodyPart
                ? `${selectedBodyPart.displayName}: ${templates.length} total · ${activeCount} active`
                : 'Select a body part to view its ROM configuration.'}
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={() =>
              selectedBodyPartId && loadTemplates(selectedBodyPartId)
            }
            disabled={
              !selectedBodyPartId ||
              loading ||
              saving ||
              activeActionId !== null
            }
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        <div
          className="clinical-template-list-toolbar"
          style={{ gridTemplateColumns: '260px minmax(220px, 1fr) 220px' }}
        >
          <select
            value={selectedBodyPartId}
            onChange={(event) => {
              setSelectedBodyPartId(event.target.value);
              if (!isEditing) {
                setForm((current) => ({
                  ...current,
                  bodyPartTemplateId: event.target.value,
                }));
              }
              setActionError('');
            }}
          >
            <option value="">Select body part</option>
            {bodyParts.map((bodyPart) => (
              <option
                key={bodyPart.bodyPartTemplateId}
                value={bodyPart.bodyPartTemplateId}
              >
                {bodyPart.displayName} ({bodyPart.bodyPartCode})
                {!bodyPart.active ? ' - Inactive' : ''}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search ROM code, name, unit, notes..."
            disabled={!selectedBodyPartId}
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>

        {!selectedBodyPartId ? (
          <div className="clinical-template-empty-state">
            Select a body part to view ROM templates.
          </div>
        ) : loading ? (
          <div className="clinical-template-empty-state">Loading ROM templates...</div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No ROM templates match the current filters.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>ROM</th>
                  <th>Unit</th>
                  <th>Side</th>
                  <th>Required</th>
                  <th>Range</th>
                  <th>Default</th>
                  <th>Increase</th>
                  <th>Repeat Limit</th>
                  <th>Status</th>
                  <th>Audit</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.romTemplateId}>
                    <td>{template.displayOrder}</td>

                    <td>
                      <strong>{template.displayName}</strong>
                      <small>{template.romCode}</small>
                      <small>Template ID: {template.romTemplateId}</small>
                      {template.adminNotes && <small>{template.adminNotes}</small>}
                    </td>

                    <td>{template.unit || 'N/A'}</td>
                    <td>{template.sideSpecific ? 'LEFT / RIGHT' : 'Not side-specific'}</td>
                    <td>{template.required ? 'Yes' : 'No'}</td>
                    <td>
                      {template.minimumValue ?? '—'} to {template.maximumValue ?? '—'}
                    </td>
                    <td>{template.defaultMeasurementValue ?? '—'}</td>
                    <td>+{template.maxDailyIncrease ?? 0} max</td>
                    <td>{template.unchangedNoteLimit ?? 0}</td>

                    <td>
                      <strong>{template.active ? 'Active' : 'Inactive'}</strong>
                    </td>

                    <td>
                      <small>
                        Updated by: {template.updatedByStaffId ?? 'N/A'}
                      </small>
                      <small>{formatDateTime(template.updatedAt)}</small>
                    </td>

                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          onClick={() => beginEdit(template)}
                          disabled={saving || activeActionId !== null}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-activate-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={
                            saving || activeActionId === template.romTemplateId
                          }
                        >
                          {activeActionId === template.romTemplateId
                            ? 'Saving...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
